import { ReactNode } from 'react';
import { Fragment } from 'react';
import dynamic from 'next/dynamic';
import { BASE_PATHS, formatMoney } from '@belong/common';
import { BulletList, Image, LinkBase, Text } from '@belong/ui';
import clsx from 'clsx';
import numeral from 'numeral';
import { UnitListingModel } from 'src/api/models';
import { useStore } from 'src/store';
import { clearHoveredListing, setHoveredListing } from 'src/store/listings';
import { getCdnListingSrc } from 'src/utils/get-cdn-listing-src';
import {
  getAddressStringCompact,
  getBathRoomString,
  getFormattedBeds,
  joinCityAndState,
} from '../../utils';
import { HomeTagProps } from '../home-tag/home-tag';
import css from './listing-unit-card.module.css';

const HomeTag = dynamic<HomeTagProps>(() => import('../home-tag/home-tag'), {
  ssr: false,
});

type ListingUnitCardProps = {
  unit: UnitListingModel;
  homeId?: string;
  favoriteButton?: ReactNode;
  fixedWidth?: boolean;
};

const imageCdnOptions = {
  edits: {
    resize: {
      // 2x resolution
      width: 2 * 345,
      height: 2 * 200,
      fit: 'cover',
    },
    rotate: null,
  },
};

export function ListingUnitCard({
  unit,
  favoriteButton,
  homeId,
  fixedWidth = true,
}: ListingUnitCardProps): JSX.Element {
  const setHovered = useStore(setHoveredListing);
  const clearHovered = useStore(clearHoveredListing);

  if (!unit) return <></>;

  const {
    address,
    basicInfo,
    isAvailableForRent,
    isDemoUnit,
    isPhotoReady,
    is3DTourReady,
    isComingSoon,
    availabilityStatus,
  } = unit;

  return (
    <LinkBase
      className={clsx(fixedWidth && css.listingCardContainer)}
      href={`${BASE_PATHS.HOME}/${joinCityAndState(address!)}/${basicInfo?.unitId}`}
      onMouseEnter={() => {
        if (homeId) setHovered(homeId);
      }}
      onMouseLeave={clearHovered}
    >
      <>
        <div
          className={clsx(
            'relative mb-xs w-full overflow-hidden rounded',
            css.imageContainer,
          )}
        >
          <div className="size-full">
            {basicInfo?.bannerImageUrl && (
              <Image
                src={getCdnListingSrc(
                  basicInfo.bannerImageUrl,
                  imageCdnOptions,
                )}
                alt=""
                className="object-cover"
              />
            )}
          </div>
          <div className={clsx(css.gradient, 'absolute inset-x-0 top-0')} />
          <div
            className={clsx(
              css.gradient,
              'absolute inset-x-0 bottom-0 rotate-180',
            )}
          />

          <div className="absolute left-2sm top-2sm">
            <HomeTag
              availabilityStatus={availabilityStatus}
              isAvailableForRent={isAvailableForRent}
              isPhotoReady={isPhotoReady}
              is3DTourReady={is3DTourReady}
              isDemoUnit={isDemoUnit}
              isComingSoon={isComingSoon}
            />
          </div>

          {favoriteButton && (
            <div className={clsx('absolute right-2sm top-2sm z-10')}>
              {favoriteButton}
            </div>
          )}

          {unit?.listingInfo?.rentAmount && (
            <div className="absolute bottom-2sm right-2sm">
              <Text fontWeight="semibold" className="text-white">
                {formatMoney(unit?.listingInfo?.rentAmount)}/Mo.
              </Text>
            </div>
          )}

          <div
            className={clsx(
              'absolute inset-0 rounded border-4 border-solid border-transparent transition duration-hover hover:border-green',
            )}
          />
        </div>
        <div className="mb-2xs font-semibold">
          <BulletList
            wrap
            items={[
              <Fragment key="beds">{getFormattedBeds(basicInfo)}</Fragment>,
              <Fragment key="baths">{getBathRoomString(basicInfo)}</Fragment>,
              <span key="sqft">
                {numeral(basicInfo?.areaInSquareFeet).format('0,0')} ft
                <sup>2</sup>
              </span>,
            ]}
          />
        </div>
        <Text>{getAddressStringCompact(unit.address!)}</Text>
      </>
    </LinkBase>
  );
}
